import { Box, Button, SimpleGrid } from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { ConditionalComponent } from '@components/functionComponent';
import { iconHandler } from '../tabs';
import BlockedNotification from '../blockedNotification';
import FieldError from '@components/fieldError';
import { Section } from '../section';
import { ButtonSkeleton } from '../skeletonButton';
import { MODE_CONSTRAINTS } from '@components/datePicker2/const';
import { DatePicker2 } from '@components/datePicker2';
import { dateFormatter } from '@helpers/utils';
import { usePopUpAssetStore, usePopUpStore } from '../../state';
import { useUploadStore } from '@pages/UploadSellout/helpers/mainState';
import { onFormatOption } from '@pages/UploadMapping/helpers/utils';
import React, { useMemo } from 'react';
import moment from 'moment';

export default function BodySection() {
    const popUpStore = usePopUpStore();
    const fileState = useUploadStore();
    const selectedFile = fileState.file;
    const isPdfFile = selectedFile?.type === 'application/pdf';
    const { timeUploadOptions, uploadTypeOptions, blockedUpload, isLoadingAsset } = usePopUpAssetStore();
    const today = new Date();
    const maxMonth = new Date(today.getFullYear(), today.getMonth(), 0);

    const {
        selectedTimeUploadType,
        onChangePopUpStore,
        selectedFormatUploadType,
        isUploading: isLoading,
        isBlockNotification,
    } = popUpStore;

    const formattedSelloutTypes = useMemo(() => {
        return onFormatOption({
            arr: timeUploadOptions,
            selectedFile,
        });
    }, [selectedFile, timeUploadOptions]);

    const formattedUploadTypes = useMemo(() => {
        return onFormatOption({
            arr: uploadTypeOptions,
            selectedFile,
            optionType: 'sellout-type',
        });
    }, [selectedFile, uploadTypeOptions]);

    return (
        <Box className="section-body">
            <ConditionalComponent statement={!isEmpty(blockedUpload) && blockedUpload?.is_block && isBlockNotification}>
                <BlockedNotification
                    blockedUpload={blockedUpload}
                    onClose={() => onChangePopUpStore({ isBlockNotification: false })}
                />
            </ConditionalComponent>

            <Section title="Pilih Tipe Sell Out">
                <SimpleGrid columns={3} gap="12px">
                    <ConditionalComponent
                        statement={!isLoadingAsset}
                        fallback={Array.from({ length: 2 }).map((_, i) => (
                            <ButtonSkeleton key={i} />
                        ))}
                    >
                        {formattedSelloutTypes.map((type) => (
                            <Button
                                key={type.value}
                                fontSize="14px"
                                disabled={isLoading || !type.isActive}
                                variant={selectedTimeUploadType === type.value ? 'solid' : 'outline'}
                                onClick={() => {
                                    onChangePopUpStore({
                                        selectedTimeUploadType: type.value,
                                    });
                                    localStorage.setItem('selectedTimeUploadType', JSON.stringify(type.value));
                                }}
                            >
                                {type.name}
                            </Button>
                        ))}
                    </ConditionalComponent>
                </SimpleGrid>
                <FieldError message={popUpStore?.error?.selectedTimeUploadType} />

                <DatePicker2
                    label=""
                    placeholder={`Pilih ${selectedTimeUploadType === 'monthly' ? 'bulan' : 'hari'} sellout`}
                    mode={selectedTimeUploadType === 'monthly' ? MODE_CONSTRAINTS.SINGLE : MODE_CONSTRAINTS.RANGE}
                    onlyMonthPicker={selectedTimeUploadType === 'monthly'}
                    maxDate={selectedTimeUploadType === 'monthly' ? maxMonth : new Date()}
                    onChange={(value) => {
                        const [startDate, endDate] = value;

                        if (selectedTimeUploadType === 'monthly') {
                            const startOfMonth = moment(new Date(startDate)).startOf('month');
                            const endOfMonth = startOfMonth.clone().endOf('month');
                            const formattedRange = [startOfMonth.format('YYYY-MM-DD'), endOfMonth.format('YYYY-MM-DD')];
                            onChangePopUpStore({ selectedTimeUploadValue: formattedRange });
                        } else {
                            const selectedDate = [
                                dateFormatter(new Date(startDate), 'YYYY-MM-DD'),
                                dateFormatter(new Date(endDate), 'YYYY-MM-DD'),
                            ];
                            onChangePopUpStore({ selectedTimeUploadValue: selectedDate });
                        }
                    }}
                    isDisabled={!selectedTimeUploadType || isLoading}
                    isDisableSelectToday
                />

                <FieldError message={popUpStore?.error?.selectedTimeUploadValue} mt="-.5rem" />
            </Section>

            <Section title="Pilih Tipe Upload">
                <SimpleGrid columns={3} gap="12px">
                    <ConditionalComponent
                        statement={!isLoadingAsset}
                        fallback={Array.from({ length: 3 }).map((_, i) => (
                            <ButtonSkeleton key={i} />
                        ))}
                    >
                        {formattedUploadTypes.map(
                            ({ name, value, logo_active, logo_disabled, logo_nonactive, isActive }) => {
                                const isSmartUpload = value === 'Smart Upload';

                                const isDisabledButton = isPdfFile ? !isSmartUpload : isLoading || !isActive;

                                const isSelected = isPdfFile ? isSmartUpload : selectedFormatUploadType === value;

                                return (
                                    <Button
                                        key={value}
                                        fontSize="14px"
                                        disabled={isDisabledButton || isLoading}
                                        variant={isSelected ? 'solid' : 'outline'}
                                        onClick={() => {
                                            if (isPdfFile && !isSmartUpload) return;

                                            onChangePopUpStore({
                                                selectedFormatUploadType: value,
                                            });
                                        }}
                                    >
                                        <Box
                                            dangerouslySetInnerHTML={{
                                                __html: iconHandler({
                                                    isActive,
                                                    isSelected,
                                                    logo_active,
                                                    logo_disabled,
                                                    logo_nonactive,
                                                }),
                                            }}
                                            sx={{
                                                '& svg': {
                                                    width: '16px',
                                                    height: '16px',
                                                },
                                            }}
                                        />

                                        {name}
                                    </Button>
                                );
                            },
                        )}
                    </ConditionalComponent>
                </SimpleGrid>
                <FieldError message={popUpStore?.error?.selectedFormatUploadType} />
            </Section>
        </Box>
    );
}
